import axios from 'axios';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CaretLeft, ImageSquare } from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';

import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';
import {
  useConfigurationQuery,
  useCompanySettingsQuery,
  useBenefitsQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import {
  useBenefitPlansQuery,
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
  DecisionIQMutation,
  useBenefitCategoryQuery,
} from 'enrollment/queries';
import { FamilySelect } from '../Shared/FamilySelect/FamilySelect';
import { PlanCard } from './PlanCard';
import { VolumeCard } from './Volume';
import { Loader } from 'theSource/components';
import {
  BenefitPlanDto,
  BenefitPlansDto,
  CoverageDto,
  CoverageLineDto,
  DependentDto,
  IncrementDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { DependentDto as DependentFamilyDto } from '../../../theSource/features/FamilyMembers/types';
import {
  getCurrentPlan,
  isCurrentPlan,
  isElectedPlan,
} from 'enrollment/features/Plans/planDetailsUtils/planDetailsUtils';
import { DeclineCard } from 'enrollment/components/DeclineCard';
import { ErrorResponseDto } from 'theSource/types';
import { ElectRequestDto } from './PlanCard/types';
import {
  UPDATE_ELECTION_QUERY_KEY,
  VALID_401K_BENEFIT_LOOKUP_CODES,
} from './PlanCard/utils';
import { queryKeyGenerator, useApiMutation } from 'theSource/utils';
import { ROUTES } from 'enrollment/app/routes';
import { ElectResponseDto } from './Volume/types';
import './PlanSelect.css';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgLifeEventId,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { LifeEventRestriction } from 'enrollment/utils/LifeEventUtils/Utils';
import '../DecisionSupportServices/DecisionIQ/components/DecisionIQInformation/DecisionIQInformation.css';
import { FooterCompareCarrierLogoBoxStyled } from './PlanCard/PlanCardStyled';
import {
  BottomNavigation,
  PageContentHeaderDisplay,
  Contribution,
  Contribution401k,
  BackLink,
} from 'enrollment/components';
import {
  VersesStyled,
  VersesTypographyStyled,
} from './PlanCard/PlanDetailsStyled';
import { usePlanInfoQuery } from 'enrollment/queries/usePlanInformationQuery';
import { selectLifeEventCompleted } from 'enrollment/services';
import { PlanLevelCostDto } from 'enrollment/queries/usePlanInformationQuery/utils';
import { CoverageLevelSelect } from './PlanCard/components';
import {
  checkCovLevelsMatch,
  dependentsEligibleForPlan,
  getShowEsignModal,
  handlePostElection,
} from './utils';
import {
  handleSavviPostElection,
  hasSavviBenefits,
  useSavviOverRideChosenBenefits,
} from 'enrollment/utils/bundledBenefitsUtils/utils';
import { DetailPillStyled } from './PlanDetailsDrawer/components/PremiumDetails/PremiumDetailsStyled';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { HospitalIndemnityDisclaimer } from './HospitalIndemnityDisclaimer';
import { selectIsAdmin } from 'theSource/features';
import { useOliveAdvisorEnabled } from 'enrollment/features/OliveAdvisor/components/OliveAdvisorEnabled';
import OliveAdvisorLoading from 'enrollment/assets/olive-advisor-loading.gif';
import { LastYearBenefitsInOe } from './LastYearBenefitsInOe/LastYearBenefitsInOe';
import { StackedVolumeCard } from './PlanCard/StackedVolume/StackedVolumeCard';
import {
  CategoryBenefitDto,
  CategoryCoverageDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { BenefitByBenefitLoader } from 'enrollment/components/BenefitByBenefitLoader';
import { IneligbleBenefitsModal } from 'enrollment/components/IneligbleBenefitsModal';
import { determineColor } from 'enrollment/utils/common/Utils';
import { setPerPayPeriodCost } from 'enrollment/components/Header/components/EnrollmentNavigation/services';
import { removeBenefitsThatAreRestricted } from '../Benefits/BenefitsPreview/GroupedBenefits/utils';
import { setBenefitData } from 'enrollment/services/Benefit/slice';
import { benefitCategorySelector } from 'enrollment/services/BenefitCategories/selectors';
import { TerminationReasonModal } from 'enrollment/components/TerminationReasonModal';

export interface BenefitDataState {
  state: {
    currentBenefitCategory: string;
    benefitData?: BenefitPlansDto;
    pendedBenefitData?: BenefitPlansDto;
    contextLookup?: string;
    fromReview?: boolean;
    benefitId?: number;
    currentCategory?: string;
  };
}

export const PlanSelect = () => {
  const { t } = useTranslation('pages.enrollmentPlanSelect');
  const { t: common } = useTranslation('common');

  const [isOliveAdvisorLoading, setIsOliveAdvisorLoading] = useState(false);
  const [oliveRecommendation, setOliveRecommendation] = useState<any>(false);
  const [oliveRecommendedPlanId, setRecommendedPlanId] = useState<
    number | null
  >(null);
  const [isOliveError, setIsOliveError] = useState(false);
  // eslint-disable-next-line
  const [oliveErrorMessage, setOliveErrorMessage] = useState('');
  const [postElection, setPostElection] = useState(false);
  const [terminationModal, setTerminationModal] = useState(false);
  const [terminationParams, setTerminationParams] = useState<ElectRequestDto>();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as BenefitDataState;
  const benefit = location.state?.benefitData?.benefit;
  const benefitName = benefit?.org_benefit_name?.toLowerCase();
  const oliveAdvisorEnabled =
    useOliveAdvisorEnabled() && benefitName === 'medical';
  const [coverage, setCoverage] = useState<
    CategoryCoverageDto | CoverageDto | undefined
  >(location.state?.benefitData?.coverage);
  const benefitData = location.state?.benefitData;
  const contextLookup = location.state?.contextLookup;
  const currentBenefitCategory = location.state?.currentBenefitCategory;
  const hubMaxCategory = location.state?.currentCategory;
  const hideEmptyCost =
    location.state?.benefitData?.benefit.hide_cost_if_empty || false;
  const fromReviewPage = location.state?.fromReview || false;
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const lifeEventCompleted = useSelector(selectLifeEventCompleted);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    contextLookup === 'life_event' ? orgPlanYearContextId : 0;
  const orgLifeEventID = useSelector(selectEnrollmentOrgLifeEventId);
  const isAdmin = useSelector(selectIsAdmin);
  const orderedBenefitCategories = useSelector(benefitCategorySelector);
  const [keepPlanData, setKeepPlanData] = useState(false);
  const baseToast = useToastDialog();
  // Wrap the toast to pass through targetClass
  const toast = {
    showToast: (options: any) =>
      baseToast.showToast({
        ...options,
        closeIcon: options.closeIcon || false,
        targetClass: options.targetClass || '',
      }),
  };
  const hasSavvi = useSavviOverRideChosenBenefits();

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  // Early fallback for missing orderedBenefitCategories
  const orderedLastBenefitCategory = 
    Array.isArray(orderedBenefitCategories) && orderedBenefitCategories.length > 0
      ? orderedBenefitCategories[orderedBenefitCategories.length - 1]
      : null;

  const AGREEMENT_TYPE_COVERAGE_LEVEL_GROUP_LOOKUPS = [
    'i_understand',
    'i_agree',
    'interested',
    'yes_no',
    'Yes_no_optout',
    'i_agree_disagree',
    'contact_me',
  ];

  // Prioritize explicit category assignments, fallback to default if none available
  const reqBenefitCategory = 
    hubMaxCategory ||
    currentBenefitCategory ||
    (orderedLastBenefitCategory?.lookup_code ?? 'uncategorized');

  const filteredDependents = () => {
    if (benefitData?.coverage) {
      return (
        benefitData.coverage.req_dependents?.map(
          (dependent: DependentDto) => dependent?.dependent_id
        ) || []
      );
    }
    return [];
  };

  const requestedDependentIds = filteredDependents();

  const [selectedDependentIds, setSelectedDependentIds] = useState<number[]>(
    []
  );
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [isNextPlanYear, setIsNextPlanYear] = useState(false);

  const { data: restrictions } = useRestrictionsQuery({
    orgLifeEventID: orgLifeEventID,
    orgPlanYearID: orgPlanYearId,
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
  });

  const { dependentsData, isLoadingDependents } = useEnrollmentFamilyQuery({
    lumosEnabled: true,
    manualEnrollmentContextOverride: enrollmentContext,
  });

  const { enrollmentContext: enrollmentContextApi } = useEnrollmentContextQuery(
    {
      enabled: true,
    }
  );

  const enrollmentContextApiData = enrollmentContextApi?.data?.filter(
    (context) => context.enrollment_type_lookup_code === 'life_event'
  )[0];
  const lifeEventPlanYears =
    enrollmentContextApiData?.subscriber_life_event?.org_plan_year_ids;
  const nextStatus = enrollmentContextApiData?.next_status;

  const showLastYearBenefitForDualLifeEvent = Boolean(
    enrollmentContext === 'life_event' &&
      isNextPlanYear &&
      lifeEventPlanYears &&
      lifeEventPlanYears.length > 1 &&
      nextStatus
  );

  const {
    data: benefitsResponse,
    isLoading: isLoadingBenefits,
    isFetching: isFetchingBenefits,
  } = useBenefitCategoryQuery({
    enrollmentContextType: String(contextLookup),
    enabled: Boolean(contextLookup),
    keepPreviousData: true,
    lumosApiHost: String(configuration?.apiHost),
    key: queryKeyGenerator(
      `${reqBenefitCategory}-${contextLookup}`,
      Number(orgPlanYearContextId)
    ),
    getParamCategory: true,
    includeEligibleDependentsData: true,
    category: reqBenefitCategory,
    orgPlanYearId: orgPlanYearContextId,
  });

  useEffect(() => {
    if (!isLoadingBenefits && benefitsResponse) {
      let filteredBenefitsData = benefitsResponse;

      dispatch(
        setPerPayPeriodCost(Number(benefitsResponse.data.cart_total || 0))
      );
      if (
        orgLifeEventID > 0 &&
        orgPlanYearId > 0 &&
        restrictions &&
        contextLookup === 'life_event'
      ) {
        filteredBenefitsData = removeBenefitsThatAreRestricted(
          restrictions.data,
          benefitsResponse
        );
      }

      const benefitFromResponse = benefitsResponse?.data?.benefits?.find(
        (b) => b.benefit.org_benefit_id === benefit?.org_benefit_id
      );

      dispatch(setBenefitData({ benefitsData: filteredBenefitsData }));
      setIsNextPlanYear(
        benefitsResponse?.data?.benefits[0].benefit.org_plan_year_id ===
          enrollmentContextApiData?.next_plan_year_id
      );
      setCoverage(benefitFromResponse?.coverage ?? undefined);
    }
  }, [
    benefitsResponse,
    restrictions,
    isLoadingBenefits,
    dispatch,
    contextLookup,
    enrollmentContextApiData?.next_plan_year_id,
    orgLifeEventID,
    orgPlanYearId,
    benefit,
  ]);

  const lumosEnabled = companySettings?.data.lumos_feature_toggle;
  const apiHost = configuration?.apiHost;

  const dependentsOnCoverage =
    coverage?.dependent_coverages?.map((depCov) => {
      const termed =
        depCov.termination_date &&
        new Date(depCov.termination_date) <= new Date();
      return termed ? -1 : depCov.dependent_id;
    }) || [];

  const [hasResetPlans, setHasResetPlans] = useState(false);
  const [comparePlans, setComparePlans] = useState<any[]>([]);

  const { plansData, isLoadingPlans, isFetchingPlans } = useBenefitPlansQuery({
    orgBenefitId: benefitData?.benefit.org_benefit_id,
    enrollmentContextType: contextLookup,
    dependentIds: selectedDependentIds,
    enabled: true,
    lumosApiHost: configuration?.apiHost,
    keepPreviousData: keepPlanData,
    includeContentDetails: true,
    includeEligibleDependentsData: true,
  });

  let plans = useMemo(
    () => plansData?.data?.plans || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.state, hasResetPlans, plansData]
  );

  const matchCoverageData = plansData?.data.match_coverage_data;
  const matchCoverageDataExists = Boolean(matchCoverageData);
  const matchCoverageDependents =
    matchCoverageData?.match_coverage_req_dep_ids || [];
  const matchDependentsExist = matchCoverageDependents?.length > 0;

  const isMissingBenefitMatchCoverage = Boolean(
    matchCoverageData?.missing_match_coverage_message
  );

  const getRequiredDependents = (
    dependents: number[],
    requiredDependents?: number[]
  ) => {
    if (requiredDependents && requiredDependents.length > 0) {
      requiredDependents.forEach((id) => {
        if (!dependents.includes(id)) {
          dependents.push(id);
        }
      });
    }
    return dependents;
  };

  const initPlanLoaded = Boolean(plansData !== undefined);
  useEffect(() => {
    if (
      Boolean(matchCoverageDependents?.length > 0) &&
      benefitData?.benefit.dependents_allowed
    ) {
      setSelectedDependentIds(matchCoverageDependents);
      setKeepPlanData(true);
    } else if (
      Boolean(plansData !== undefined) &&
      benefitData?.coverage &&
      requestedDependentIds
    ) {
      const dependents = benefitData?.benefit.dependents_allowed
        ? getRequiredDependents(
            requestedDependentIds,
            plansData?.data.required_dependent_ids
          )
        : [];
      setSelectedDependentIds(dependents);
      setKeepPlanData(true);
    } else if (Boolean(plansData !== undefined) && !benefitData?.coverage) {
      if (benefit?.dependents_allowed && initPlanLoaded) {
        const dependents = dependentsData?.data?.dependents
          ?.filter((dependent: DependentFamilyDto) => {
            return plansData?.data.plans?.some((plan) =>
              plan?.allowed_dependent_ids?.includes(dependent?.id)
            );
          })
          ?.map((dependent) => dependent?.id);
        const fullDependents = getRequiredDependents(
          dependents || [],
          plansData?.data.required_dependent_ids
        );
        if (fullDependents) {
          setSelectedDependentIds(fullDependents);
        }
        setKeepPlanData(true);
      }
    }
    // eslint-disable-next-line
  }, [matchDependentsExist, initPlanLoaded]);

  const benefitCategory = benefit?.org_benefit_category || '';
  const preElectedPlan = Boolean(
    contextLookup !== 'life_event' &&
      benefitData?.benefit?.org_benefit_id &&
      benefitData?.coverage &&
      benefitsResponse?.data?.active_enrollment_benefit_status &&
      !benefitsResponse?.data?.active_enrollment_benefit_status[
        benefitData.benefit.org_benefit_id.toString() || ''
      ]
  );

  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });

  const subscribeZipCode = subscriber?.data?.zip_code?.value;

  const {
    isLoadingSubmission: isLoadingRecommendation,
    SurveyResponsesRequest,
  } = DecisionIQMutation(
    benefitData?.benefit.org_benefit_id || 0,
    String(configuration?.apiHost),
    selectedDependentIds,
    true // reload plans after submission
  );

  const backPath = `${ROUTES.enrollment.benefits.path}`;
  const benefitScroll = benefitsResponse?.data.benefits
    .filter(
      (benefit) =>
        benefit.benefit.org_benefit_category ===
        benefit?.benefit.org_benefit_category
    )
    .sort((a, b) => a.benefit.display_order - b.benefit.display_order)
    .find(
      (benefit) =>
        benefit.benefit.org_benefit_id === benefitData?.benefit.org_benefit_id
    )?.benefit.benefit_lookup_code;

  const isHsaNewExperienceEnabled =
    companySettings?.data?.hsa_new_experience_feature_toggle &&
    benefit?.benefit_lookup_code === 'hsa';
  const hsaElectApiVersion = isHsaNewExperienceEnabled ? 'v3' : 'v2';
  const { isLoading: isEditing, mutate: updateElection } = useApiMutation<
    ElectResponseDto,
    ErrorResponseDto,
    ElectRequestDto
  >({
    queryKey: UPDATE_ELECTION_QUERY_KEY,
    queryKeysToInvalidate: queryKeyGenerator(
      fromReviewPage && orderedLastBenefitCategory
        ? `${orderedLastBenefitCategory.lookup_code}-${contextLookup}`
        : `${reqBenefitCategory}-${contextLookup}`,
      Number(orgPlanYearContextId)
    ),
    method: 'put',
    endpoint: `${apiHost}/${hsaElectApiVersion}/self_service/benefits/elect`,
    onSuccess: () => setPostElection(true),
    getSuccessMessage: () => common('addedToCart'),
    getErrorMessage: (error) => error.response?.data?.details,
    toastRef: {
      showToast: (options: any) =>
        toast.showToast({
          ...options,
          closeIcon: options.status === 'success' ? false : true,
          targetClass: options.status === 'success' ? 'toast-cart-target' : '',
        }),
    },
  });

  const disableBtns = isEditing || false;

  const electPlan = (params: ElectRequestDto) => {
    const electedPlan = sortedPlans.find(
      (plan) => plan.plan_id === params.election.org_plan_id
    );

    if (
      electedPlan?.requires_termination_reason &&
      !params.termination_reason_id
    ) {
      setTerminationParams(params);
      setTerminationModal(true);
    } else {
      updateElection(params);
    }
  };

  let canIncrease = true;
  let canDecrease = true;
  if (restrictions && benefit && contextLookup === 'life_event') {
    const LERestriction = new LifeEventRestriction({
      lifeEventRestrictions: restrictions.data,
      orgBenefit: benefit,
      currentCoverage: coverage,
    });
    canIncrease = LERestriction.canIncrease;
    canDecrease = LERestriction.canDecrease;
  }

  const declinePlans = plans?.filter(
    (plan) => plan.plan_type === 'decline' || plan.waived
  );

  const nonDeclinePlans = plans?.filter(
    (plan) => plan.plan_type !== 'decline' && !plan.waived
  );

  const eligibleDependents =
    nonDeclinePlans && benefit?.dependents_allowed
      ? dependentsData?.data?.dependents
          ?.filter((dependent: DependentFamilyDto) => {
            if (Array?.isArray(plans)) {
              return plans?.some((plan) =>
                plan?.allowed_dependent_ids?.includes(dependent?.id)
              );
            }
            return [];
          })
          ?.map((dependent) => dependent?.id)
      : undefined;

  let filteredPlans: BenefitPlanDto[] = useMemo(() => {
    return [];
  }, [plans]); // eslint-disable-line
  let allCovLevelsMatch = false;
  const [selectedCovLevel, setSelectedCovLevel] = useState(
    coverage?.coverage_level_lookup_code || ''
  );

  if (!benefit?.dependents_allowed) {
    nonDeclinePlans.forEach((plan) => {
      if (!filteredPlans.some((p) => p.plan_id === plan.plan_id)) {
        filteredPlans.push(plan);
      }
    });

    allCovLevelsMatch = checkCovLevelsMatch(nonDeclinePlans);
  } else {
    filteredPlans = nonDeclinePlans;
  }
  const { planInfoData, isLoadingPlanInfo, isFetchingPlanInfo } =
    usePlanInfoQuery({
      orgPlanYearId,
      enrollmentContext,
      lifeEventCompleted,
      orgPlanId: filteredPlans[0]?.plan_id,
      enabled: !benefit?.dependents_allowed && filteredPlans.length > 0,
    });

  const showCovLevSelect =
    !isLoadingPlanInfo &&
    !isFetchingPlanInfo &&
    !benefit?.dependents_allowed &&
    allCovLevelsMatch;

  const { data: benefitsData } = useBenefitsQuery({ enabled: true });

  const queryBenefitsData = benefitsData?.data?.current?.filter((item) => {
    return item?.benefit_lookup_code === benefit?.benefit_lookup_code;
  });

  const subscriberProfile = subscriber?.data?.subscriber_profile;

  const isPlanDeclined = (plan: BenefitPlanDto) => {
    if (!plan) {
      return false;
    }

    if (benefitData?.coverage) {
      if (benefitData.coverage.req_coverage_level_lookup_code === 'decline') {
        return true;
      } else if (
        benefitData.coverage.req_coverage_level_lookup_code === 'Enrolled'
      ) {
        return false;
      }
    }
    return plan.plan_type === 'decline' || plan.waived;
  };

  const anyPlanDeclined = filteredPlans?.some((plan) => isPlanDeclined(plan));

  useEffect(() => {
    if (
      (benefitData?.coverage === null || preElectedPlan || anyPlanDeclined) &&
      eligibleDependents &&
      eligibleDependents.length &&
      !selectedDependentIds.length &&
      !hasUserSelected
    ) {
      setSelectedDependentIds(eligibleDependents);
    }
  }, [
    eligibleDependents,
    hasUserSelected,
    anyPlanDeclined,
    benefitData?.coverage,
    preElectedPlan,
    selectedDependentIds.length,
  ]);

  const onDependentSelect = (id?: number) => {
    if (!id) {
      return;
    }
    setHasUserSelected(true);

    if (selectedDependentIds.includes(id)) {
      // Remove from selected dependents (create a new array)
      setSelectedDependentIds(
        selectedDependentIds.filter((depId) => depId !== id)
      );
      setHasResetPlans(true);
    } else {
      // Add to selected dependents (create a new array)
      setSelectedDependentIds([...selectedDependentIds, id]);
      setHasResetPlans(true);
    }
  };

  const color = determineColor(benefitCategory);
  let currentPlan;
  if (coverage?.org_plan_id) {
    currentPlan = getCurrentPlan(coverage?.org_plan_id, plans);
  }

  const handleComparePlans = (planDetails: any) => {
    let updatedComparePlans: any[] = [];

    if (planDetails.add) {
      updatedComparePlans = [...comparePlans, planDetails.plan];
      setComparePlans(updatedComparePlans);
    } else if (planDetails.remove) {
      updatedComparePlans = comparePlans.filter(
        (plan: any) => plan.plan_id !== planDetails.plan.plan_id
      );
      setComparePlans(updatedComparePlans);
    }

    if (updatedComparePlans.length === 1) {
      document
        .getElementById('compare-plans-button')
        ?.setAttribute('disabled', 'true');
    } else if (updatedComparePlans.length > 1) {
      document
        .getElementById('compare-plans-button')
        ?.removeAttribute('disabled');
    }
  };

  const goToComparePlansPage = () => {
    navigate(`/enrollment/${ROUTES.enrollment.comparePlans.path}`, {
      state: {
        comparePlans: comparePlans,
        benefit: benefit,
        coverage: coverage,
        contextLookup: contextLookup,
        orgBenefitId: benefitData?.benefit.org_benefit_id,
        currentBenefitCategory: reqBenefitCategory,
        benefitData: benefitData,
        selectedDependentIds: selectedDependentIds,
      },
    });
  };

  const buildVerses = () => {
    return (
      <>
        <VersesTypographyStyled>VS</VersesTypographyStyled>
        <VersesStyled></VersesStyled>
      </>
    );
  };
  // TODO: move this to utils
  const displayPlanDetails = (plan: BenefitPlanDto, index: number) => {
    return (
      <div className="d-flex align-items-center">
        {index === 2 && buildVerses()}

        <div className="d-flex flex-column">
          <div>
            {plan?.carrier_logo_url && plan?.carrier_logo_url !== null ? (
              <FooterCompareCarrierLogoBoxStyled>
                <img
                  src={plan.carrier_logo_url}
                  alt={plan.carrier || t('defaultLogoAltText')}
                  data-testid="carrier-logo"
                  style={{ maxHeight: '100%' }}
                />
              </FooterCompareCarrierLogoBoxStyled>
            ) : (
              <FooterCompareCarrierLogoBoxStyled>
                <ImageSquare size={24} weight="duotone" color="#56638a" />
                <Typography color="#56638a">{plan.carrier}</Typography>
              </FooterCompareCarrierLogoBoxStyled>
            )}
          </div>
          <div>
            <Typography
              variant="subtitle2"
              style={{ color: '#55575E' }}
              className="text-capitalize"
            >{`${benefit?.benefit_lookup_code}: ${plan.plan_name}`}</Typography>
          </div>
        </div>

        {index === 0 && buildVerses()}
      </div>
    );
  };

  const showComparedPlans = () => {
    return (
      <BottomNavigation
        comparePlansFooter={true}
        onClickEvent={() => goToComparePlansPage()}
        translatedPage="pages.enrollmentComparePlans"
        optionalClass="plan-compare-footer"
        comparePlans={comparePlans}
        orgBenefitId={benefitData?.benefit.org_benefit_id}
      >
        <div className="d-flex">
          {comparePlans.map((plan, index) => {
            return displayPlanDetails(plan, index);
          })}
        </div>
      </BottomNavigation>
    );
  };

  useEffect(() => {
    if (
      oliveAdvisorEnabled &&
      plans.length &&
      planInfoData?.data?.plan_level_costs
    ) {
      const fetchOliveRecommendation = async () => {
        setIsOliveAdvisorLoading(true);
        try {
          const response = await axios.post(
            `${apiHost}/v2/self_service/olive_advisor/submit_query`,
            {
              user_query: 'recommend_plan',
              subscriber_id: subscriber?.data?.id.value,
              org_benefit_id: benefitData?.benefit.org_benefit_id,
              dependent_ids: selectedDependentIds,
              enrollment_context_type: enrollmentContext,
            }
          );

          const {
            olive_response: oliveResponse,
            recommended_plan_id: recommendedPlanId,
          } = response.data.data;

          const responseError = response.data.data.Error;
          if (responseError) {
            setIsOliveError(true);
            // setOliveErrorMessage(responseError); // saving code in case olive error message is configured to provide a more detailed message in the future.
            setOliveErrorMessage(t('oliveAdvisor:recommend.error'));
          }

          const orgBenefitId = benefitData?.benefit.org_benefit_id;
          setOliveRecommendation({
            oliveResponse,
            recommendedPlanId,
            orgBenefitId,
          });
          setRecommendedPlanId(Number(recommendedPlanId));
          setIsOliveAdvisorLoading(false);
        } catch (error) {
          setIsOliveAdvisorLoading(false);
          setIsOliveError(true);
        }
      };

      fetchOliveRecommendation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    oliveAdvisorEnabled,
    plans,
    planInfoData,
    selectedDependentIds,
    enrollmentContext,
    apiHost,
    subscriber,
    benefitData,
  ]);

  useEffect(() => {
    if (
      postElection &&
      benefitsResponse &&
      !(isFetchingBenefits || isLoadingBenefits)
    ) {
      const categoryLookup = benefit?.org_benefit_category;
      const handlePostElectionRouter =
        hasSavvi &&
        hasSavviBenefits(
          benefitsResponse.data.benefits,
          benefitsResponse.data.bundled_org_benefit_ids
        )
          ? handleSavviPostElection
          : handlePostElection;
      const postElectionValues = handlePostElectionRouter(
        String(contextLookup),
        Number(orgPlanYearContextId),
        benefitsResponse,
        String(categoryLookup),
        Number(benefitData?.benefit.org_benefit_id)
      );
      const benefitScroll = postElectionValues.benefitScroll;
      // reset the states on move from plan to plan
      setPostElection(false);
      setHasResetPlans(true);
      setKeepPlanData(false);
      setSelectedDependentIds([]);
      plans = []; // eslint-disable-line

      // never navigate to the review page. Kick the user to hub or next benefit
      navigate(postElectionValues.navPath, {
        state: {
          benefitData: postElectionValues.benefitData,
          pendedBenefitData: postElectionValues.pendedBenefitData,
          contextLookup: contextLookup,
          fromCategory: categoryLookup,
          currentBenefitCategory: currentBenefitCategory,
          currentCategory: hubMaxCategory,
          benefitId: postElectionValues.benefitData?.benefit?.org_benefit_id,
          fromReview: fromReviewPage,
          scrollToBenefit: benefitScroll,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postElection, benefitsResponse, isFetchingBenefits, isLoadingBenefits]);

  useEffect(() => {
    if (plansData && hasResetPlans) {
      plans = plansData.data?.plans || []; // eslint-disable-line
      setHasResetPlans(false);
    }
  }, [hasResetPlans, plansData]);

  const sortedPlans = useMemo(() => {
    let recommendedPlan;
    let selectedPlan;
    const otherPlans: BenefitPlanDto[] = [];

    // Recompute sortedPlans whenever these values change
    filteredPlans.forEach((plan) => {
      if (
        isCurrentPlan(coverage?.org_plan_id, plan.plan_id) &&
        requestedDependentIds?.toString() === selectedDependentIds?.toString()
      ) {
        selectedPlan = plan;
      } else if (plan.plan_id === plansData?.data.recommendation?.plan_id) {
        recommendedPlan = plan;
      } else if (plan.plan_id === oliveRecommendedPlanId) {
        recommendedPlan = plan;
      } else {
        otherPlans.push(plan);
      }
    });

    // Unshift recommended and selected plans to the beginning
    const finalSortedPlans = [...otherPlans].sort((a, b) => {
      return (a.display_order || 0) - (b.display_order || 0);
    });
    recommendedPlan && finalSortedPlans.unshift(recommendedPlan);
    selectedPlan && finalSortedPlans.unshift(selectedPlan);

    return finalSortedPlans;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    oliveRecommendedPlanId,
    filteredPlans,
    coverage,
    requestedDependentIds,
    selectedDependentIds,
    plansData,
    plans,
  ]);

  // Improved isOnlyPlan logic: only one unique non-decline plan (by plan_name), plus possibly a decline plan
  const uniqueNonDeclinePlanNames = [
    ...new Set(
      plans
        .filter((plan) => plan.plan_type !== 'decline' && !plan.waived)
        .map((plan) => plan.plan_name)
    ),
  ];

  const isOnlyPlan = uniqueNonDeclinePlanNames.length === 1;

  if (
    !lumosEnabled ||
    isLoadingConfiguration ||
    isLoadingDependents ||
    isLoadingPlans ||
    isLoadingBenefits ||
    isFetchingBenefits ||
    hasResetPlans
  ) {
    return <BenefitByBenefitLoader />;
  }

  const dependentsToShow = dependentsEligibleForPlan(
    matchDependentsExist,
    dependentsData?.data.dependents || [],
    matchCoverageData?.match_coverage_req_dep_ids || []
  );

  const isHospitalIndemnity = Boolean(
    benefitData?.benefit.show_hospital_compliance
  );

  const showCoverageLevel =
    benefit?.benefit_lookup_code === 'medical' ||
    benefit?.benefit_lookup_code === 'vision' ||
    benefit?.benefit_lookup_code === 'dental' ||
    benefit?.benefit_lookup_code === 'rx';
  const handleCompareBtns = () => {
    return !(plans.length > 1);
  };

  const LastYearBenefitsInOeDisplayCondition =
    filteredPlans.length > 0 &&
    !isMissingBenefitMatchCoverage &&
    showCoverageLevel;

  // Determine if any plan will show the selected-indicator (PlanCard banner)
  const shouldShowSelectedIndicator = plans.some(
    (plan) =>
      (isElectedPlan(
        coverage,
        plan.plan_id,
        requestedDependentIds,
        selectedDependentIds
      ) ||
        plan.plan_id === plansData?.data.recommendation?.plan_id) &&
      coverage?.coverage_level_lookup_code === selectedCovLevel
  );

  return (
    <div data-testid="plan-select" className="plan-select">
      <BackLink
        backLinkValue={t('common:back')}
        fixedPath={
          fromReviewPage
            ? `/enrollment/${ROUTES.enrollment.review.path}`
            : `/enrollment/${backPath}`
        }
        internal
        scrollToBenefit={benefitScroll}
        underline={false}
        IconComponent={CaretLeft}
        testId="back-button"
      />
      <main id="mainContent">
        {benefit && (
          <>
            <PageContentHeaderDisplay
              benefit={benefit as unknown as CategoryBenefitDto}
              lumosApiHost={apiHost}
              pageContentLookupCode="ENROLL_COVERAGE"
              orgPlanYearId={benefit.org_plan_year_id}
            />
            {LastYearBenefitsInOeDisplayCondition && queryBenefitsData && (
              <LastYearBenefitsInOe
                queryBenefitsData={queryBenefitsData}
                color={color}
                showLastYearBenefitForDualLifeEvent={
                  showLastYearBenefitForDualLifeEvent
                }
              />
            )}
          </>
        )}
        {benefitsResponse?.data.validation_errors && (
          <IneligbleBenefitsModal
            enrollmentContextType={String(contextLookup)}
            orgPlanYearContextId={Number(orgPlanYearContextId)}
            currentCategory={hubMaxCategory}
            data={benefitsResponse}
          />
        )}

        {benefit && isMissingBenefitMatchCoverage && (
          <div className="missingMatchCoverageMessageWrapper">
            <PSBannerWrapper
              data-testid="missing_match_benefit_message"
              message={matchCoverageData?.missing_match_coverage_message || ''}
              level="info"
              icon
              extraClass="w-100"
            />
          </div>
        )}

        {isHospitalIndemnity && <HospitalIndemnityDisclaimer />}
        {benefit?.dependents_allowed || matchCoverageDataExists ? (
          <>
            {!matchCoverageDataExists && (
              <>
                <FamilySelect
                  dependents={dependentsToShow}
                  selectedDependentIds={selectedDependentIds}
                  dependentsOnCoverage={dependentsOnCoverage}
                  subscriberProfile={subscriberProfile}
                  onChange={onDependentSelect}
                  requiredDependents={plansData?.data.required_dependent_ids}
                  eligibleDependents={eligibleDependents}
                  canIncrease={canIncrease}
                  canDecrease={canDecrease}
                  isDependentOnlyPlan={
                    currentPlan?.is_dependent_only_plan || false
                  }
                  hideCheckBox={matchDependentsExist}
                />
              </>
            )}

            {matchCoverageData?.match_coverage_exists_message && (
              <PSBannerWrapper
                data-testid="match_coverage_exists_message"
                message={matchCoverageData?.match_coverage_exists_message}
                level="info"
                icon
              />
            )}
            {filteredPlans.length > 0 &&
              !isMissingBenefitMatchCoverage &&
              showCoverageLevel &&
              queryBenefitsData && (
                <div>
                  <div className="d-md-flex align-items-center mt-3_5 pt-2">
                    <h3 className="d-block d-md-inline-block mr-md-3_5 mb-md-0">
                      {t('coverage')}
                    </h3>
                    <DetailPillStyled $isSelected={true}>
                      {filteredPlans[0].level_name}
                    </DetailPillStyled>
                  </div>
                </div>
              )}
          </>
        ) : (
          !shouldShowSelectedIndicator && (
            <h2 className="sr-only">{t('availablePlans')}</h2>
          )
        )}
        {showCovLevSelect && !matchCoverageData && (
          <div style={{ width: '328px' }}>
            <CoverageLevelSelect
              planLevelCosts={
                planInfoData?.data.plan_level_costs as PlanLevelCostDto
              }
              selectedCovLevel={selectedCovLevel}
              setSelectedCovLevel={setSelectedCovLevel}
              hideLabel
            />
          </div>
        )}
        {benefit?.benefit_class === 'volume' && restrictions && (
          <div
            id="cannot-delete-notice"
            className="blue-alert mt-4"
            data-testid="limited-volume-notice"
          >
            <i
              className="ph ph-info mr-2 my-sm-auto"
              aria-hidden="true"
              role="presentation"
            ></i>
            <p className="mb-0">
              {t('lifeEventInfo1')}
              {benefit.org_benefit_name}
              {t('lifeEventInfo2')}
            </p>
          </div>
        )}
        <div>
          {isOliveAdvisorLoading && (
            <div
              style={{
                paddingBottom: '16px',
                textAlign: 'center',
              }}
            >
              <img src={OliveAdvisorLoading} alt="olive-adiviser" />
              <p>
                Olive is finding plans that might be a good fit for you.
                <br />
                This will only take a few moments.
              </p>
            </div>
          )}
          {isOliveError && (
            <PSBannerWrapper
              data-testid="oliveErrorMessage"
              message={t('oliveAdvisor:recommend.error')}
              level="error"
              icon
              extraClass="my-3"
            />
          )}
          {!isFetchingPlans &&
            !isLoadingRecommendation &&
            !isMissingBenefitMatchCoverage && (
              <div className="row mx-0 p-0">
                {sortedPlans?.map((plan, index) => {
                  const commonProps = {
                    plan,
                    orgBenefitId: benefit?.org_benefit_id,
                    enrollmentContextType: contextLookup,
                    selectedDependentIds,
                    coverage,
                    color,
                    benefitCategory,
                    preElectedPlan: preElectedPlan,
                    benefitLookup: String(benefit?.benefit_lookup_code),
                    updateElection: electPlan,
                    showEsignatureModal: getShowEsignModal(
                      plan?.esign_data,
                      coverage,
                      isAdmin
                    ),
                    orgPlanYearId: Number(benefit?.org_plan_year_id),
                    hideEmptyCost: hideEmptyCost,
                    confirmButtonOverrideLabel:
                      plan.level_group_lookup_code &&
                      AGREEMENT_TYPE_COVERAGE_LEVEL_GROUP_LOOKUPS.includes(
                        plan.level_group_lookup_code
                      )
                        ? plan.level_name
                        : undefined,
                  };
                  if (benefit?.benefit_class === 'volume' && plan.increments) {
                    if (plan.stacked_coverage_display === 'individual') {
                      const coverageLines =
                        coverage?.coverage_lines || ([] as CoverageLineDto[]);
                      if (
                        coverageLines.length <
                        Object.keys(
                          plan.increments as { [key: string]: IncrementDto[] }
                        ).length
                      ) {
                        coverageLines.push({
                          id: 0,
                          change_effective_date: String(
                            plan.new_line_original_effective_date
                          ),
                          original_effective_date: String(
                            plan.new_line_original_effective_date
                          ),
                          req_increments: '-1',
                          in_current_plan_year: true,
                        } as CoverageLineDto);
                      }
                      coverageLines.sort((a, b) =>
                        new Date(a.change_effective_date) <
                        new Date(b.change_effective_date)
                          ? -1
                          : 1
                      );
                      return (
                        <StackedVolumeCard
                          key={index}
                          {...commonProps}
                          increments={
                            plan.increments as { [key: string]: IncrementDto[] }
                          }
                          coverageLines={coverageLines}
                        />
                      );
                    } else {
                      return (
                        <VolumeCard
                          key={index}
                          {...commonProps}
                          increments={plan.increments as IncrementDto[]}
                        />
                      );
                    }
                  } else if (
                    benefit &&
                    VALID_401K_BENEFIT_LOOKUP_CODES.includes(
                      benefit.benefit_lookup_code
                    )
                  ) {
                    return (
                      <Contribution401k
                        key={index}
                        {...commonProps}
                        benefit={benefit}
                        benefitPlans={plans}
                      />
                    );
                  } else if (benefit?.benefit_class === 'contribution') {
                    return (
                      <Contribution
                        key={index}
                        {...commonProps}
                        benefit={benefit}
                        benefitPlans={plans}
                      />
                    );
                  } else {
                    return (
                      <PlanCard
                        key={index}
                        {...commonProps}
                        subscriberProfile={subscriberProfile}
                        dependents={dependentsData}
                        selected={isElectedPlan(
                          coverage,
                          plan.plan_id,
                          requestedDependentIds,
                          selectedDependentIds
                        )}
                        disabled={disableBtns}
                        disableCompareBtns={handleCompareBtns()}
                        isRecommendedPlan={
                          plan.plan_id ===
                          plansData?.data.recommendation?.plan_id
                        }
                        setComparePlans={handleComparePlans}
                        onRecommendationSurveySubmit={SurveyResponsesRequest}
                        pcpMembers={coverage?.pcp_members}
                        isDepSelect={benefit?.dependents_allowed}
                        parentCovLevel={
                          showCovLevSelect ? selectedCovLevel : undefined
                        }
                        subscribeZipCode={subscribeZipCode}
                        isOnlyPlan={isOnlyPlan}
                        oliveRecommends={
                          plan.plan_id === oliveRecommendedPlanId
                        }
                        reasons={oliveRecommendation.oliveResponse}
                      />
                    );
                  }
                })}
              </div>
            )}
          {(isFetchingPlans || isLoadingRecommendation) && (
            <div>
              <br />
              <Loader />
            </div>
          )}
        </div>

        {!isFetchingPlans &&
          !isLoadingRecommendation &&
          declinePlans.length > 0 && (
            <DeclineCard
              declineText={`${t('common:enrollment:declineStatement')} ${benefit?.org_benefit_name}?`}
              declinePlans={declinePlans}
              orgBenefit={benefit}
              enrollmentContextType={contextLookup}
              handleDeclineBenefit={electPlan}
            ></DeclineCard>
          )}
        {plansData?.data?.termination_reasons && (
          <TerminationReasonModal
            handleElectPlan={electPlan}
            terminationReasons={plansData?.data?.termination_reasons}
            modalOpen={terminationModal}
            setModalOpen={setTerminationModal}
            params={terminationParams}
          />
        )}
        {comparePlans.length > 0 ? showComparedPlans() : null}
      </main>
    </div>
  );
};
